import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@adsk/bim360-matrix-react-button';
import SplitButton from '@adsk/bim360-matrix-react-split-button';
import './_ButtonGroup.scss';

/**
 * ButtonGroup component - renders a Button or a SplitButton (depends on provided options length).
 * when a list of more than 1 option is provided, the SplitButton's options act as buttons (meaning that selecting an
 * option also triggers an action, as if the button was clicked). There's no selection indication in the UI and the
 * main action (the first option in the options array) remains the same.
 */
export default class ButtonGroup extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this._handleOptionSelection = this._handleOptionSelection.bind(this);
    this._handleMainOptionSelect = this._handleMainOptionSelect.bind(this);
  }

  /**
   * Option select handler - triggers `onSelect` with the selected value and option object
   */
  _handleOptionSelection(option) {
    this.props.onSelect(option.value, option);
  }

  /**
   * Trigger main action select
   */
  _handleMainOptionSelect() {
    this._handleOptionSelection(this.props.options[0]);
  }

  /**
   * Render a Button or a SplitButton component based on the options length
   */
  _renderButtonComponent() {
    const { options, isSaving, savingText, actionButtonProps } = this.props;
    const styleType = actionButtonProps.styleType || Button.StyleType.PRIMARY;
    const isHighlight = styleType === Button.StyleType.PRIMARY;
    if (options.length === 1) {
      return (
        <Button
          actionButtonProps={actionButtonProps}
          className="ButtonGroup__button"
          styleType={styleType}
          onClick={this._handleMainOptionSelect}
          disabled={isSaving}
        >
          {isSaving ? savingText : options[0].label}
        </Button>
      );
    } else {
      return (
        <SplitButton
          actionButtonProps={actionButtonProps}
          options={options}
          defaultOption={options[0]}
          isDropdownOnLeft={false}
          actionButtonClassName={classNames('ButtonGroup__action-button', {
            'ButtonGroup__action-button--highlight': isHighlight
          })}
          dropdownButtonClassName={classNames('ButtonGroup__dropdown-button', {
            'ButtonGroup__dropdown-button--highlight': isHighlight
          })}
          actionButtonLabel={isSaving ? savingText : options[0].label}
          onDropdownSelect={this._handleOptionSelection}
          onActionButtonClick={this._handleMainOptionSelect}
          isDisabled={isSaving}
          additionalDropdownProps={{ triggerChangeOnReselect: true }}
        />
      );
    }
  }

  /**
   * render
   */
  render() {
    return this._renderButtonComponent();
  }
}

const dropdownOptionPropType = PropTypes.shape({
  /**
   * The option's value
   */
  value: PropTypes.string.isRequired,
  /**
   * The option's label to display
   */
  label: PropTypes.string.isRequired
});

ButtonGroup.propTypes = {
  /**
   * An array of options (each in the shape of a single dropdown option)
   */
  options: PropTypes.arrayOf(dropdownOptionPropType).isRequired,
  /**
   * On option select handler function
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * `isSaving` flag - when true, the button (and dropdown) will be disabled and the provided `savingText` will show
   */
  isSaving: PropTypes.bool,
  /**
   * The properties to be sent to the action button component of the `SplitButton`
   */
  actionButtonProps: PropTypes.object,
  /**
   * The text to display when `isSaving` is true
   */
  savingText: PropTypes.string.isRequired
};

ButtonGroup.defaultProps = {
  actionButtonProps: {}
};
